import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Typography from "./Typography";
import Button from "./Button";
import IconBadge from "./IconBadge";
import { cn } from "../../utils/classNames";

const SIZE_CLASSES = {
  default: "sp-slide-panel--default",
  wide: "sp-slide-panel--wide",
};

const SlideOutPanel = ({
  open,
  onClose,
  title,
  subtitle = null,
  icon: Icon = null,
  headerContent = null,
  size = "default",
  children,
  ariaLabel = null,
}) => {
  const panelRef = useRef(null);

  // Focus moves into the panel on open, stays there while it is open, and returns to the opener on close.
  useEffect(() => {
    if (!open) return undefined;

    const opener = document.activeElement;
    panelRef.current?.querySelector("button")?.focus();

    const onTab = (event) => {
      const panel = panelRef.current;
      if (event.key !== "Tab" || !panel) return;
      const focusable = Array.from(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onTab);
    return () => {
      document.removeEventListener("keydown", onTab);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    document.body.classList.add("overflow-hidden");

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-foreground/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />

          <motion.aside
            ref={panelRef}
            className={cn(
              "sp-slide-panel fixed inset-x-4 bottom-4 z-50 flex flex-col overflow-hidden border border-insight-border bg-insight shadow-none rounded-xl sm:inset-x-auto sm:right-6 sm:top-24",
              SIZE_CLASSES[size] || SIZE_CLASSES.default
            )}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.35, ease: [0.0, 0.0, 0.2, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel || title}
          >
            <div className="relative flex h-full min-h-0 flex-col">
              <Button
                variant="space"
                size="icon"
                onClick={onClose}
                className="absolute right-6 top-5 z-20 h-11 w-11 rounded-md"
                aria-label="Close panel"
              >
                <X className="h-5 w-5" />
              </Button>

              <div className="border-b border-insight-border px-6 pb-5 pt-6 bg-insight">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 pr-12 text-foreground">
                    {Icon && (
                      <IconBadge size="lg">
                        <Icon className="h-5 w-5 text-primary" />
                      </IconBadge>
                    )}
                    <div>
                      <Typography variant="h3" className="text-foreground">
                        {title}
                      </Typography>
                      {subtitle && (
                        <Typography variant="caption" className="text-muted-foreground text-label-light">
                          {subtitle}
                        </Typography>
                      )}
                    </div>
                  </div>
                  {headerContent}
                </div>
              </div>

              <div className="custom-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-7 pt-6 space-y-6">
                {children}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

SlideOutPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType,
  headerContent: PropTypes.node,
  size: PropTypes.oneOf(["default", "wide"]),
  children: PropTypes.node,
  ariaLabel: PropTypes.string,
};

export default SlideOutPanel;
